import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Building2, Users, MapPin, Calendar, Search, X, Loader2 } from 'lucide-react';

const getIcon = (type) => {
  switch (type) {
    case 'organization':
      return Building2;
    case 'contact':
      return Users;
    case 'venue':
    case 'location':
      return MapPin;
    case 'event':
      return Calendar;
    default:
      return Search;
  }
};

const getIconColor = (type) => {
  switch (type) {
    case 'organization':
      return 'text-cyan-400';
    case 'contact':
      return 'text-cyan-400';
    case 'venue':
    case 'location':
      return 'text-amber-400';
    case 'event':
      return 'text-emerald-400';
    default:
      return 'text-muted-foreground';
  }
};

const getRoute = (type, id) => {
  switch (type) {
    case 'organization':
      return `/organizations/${id}`;
    case 'contact':
      return `/contacts/${id}`;
    case 'venue':
    case 'location':
      return `/locations/${id}`;
    case 'event':
      return `/events/${id}`;
    default:
      return '/';
  }
};

export const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchAPI.global(query, 15);
        setResults(response.data.results);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleSelect = (result) => {
    navigate(getRoute(result.type, result.id));
    setQuery('');
    setIsOpen(false);
    setResults([]);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search organizations, contacts, venues..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10 bg-background/50 border-border/50 focus:border-primary"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50 glass border border-border/50 rounded-lg shadow-lg overflow-hidden">
          {results.length > 0 ? (
            <ScrollArea className="max-h-80">
              <div className="py-2">
                {results.map((result, index) => {
                  const Icon = getIcon(result.type);
                  const iconColor = getIconColor(result.type);
                  const isSelected = index === selectedIndex;

                  return (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => handleSelect(result)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected ? 'bg-accent' : 'hover:bg-accent/50'
                      }`}
                      data-testid={`search-result-${result.type}-${result.id}`}
                    >
                      <div className={`p-1.5 rounded-md bg-muted/50`}>
                        <Icon className={`h-4 w-4 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{result.name}</p>
                        {result.subtitle && (
                          <p className="text-sm text-muted-foreground truncate">
                            {result.subtitle}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground capitalize">
                        {result.type}
                      </span>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          ) : !loading ? (
            <div className="px-4 py-8 text-center text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No results found for "{query}"</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
